import { z } from 'zod';

const Count = z.number().int().nonnegative();
const Bytes = z.number().int().nonnegative();

export const StorageAccountCapacity = z.object({
  accountId: z.number().int().positive(),
  name: z.string(),
  email: z.string(),
  color: z.string(),
  folders: Count,
  threads: Count,
  messages: Count,
  fullBodies: Count,
  headerOnlyBodies: Count,
  unavailableBodies: Count,
  providerBytes: Bytes,
  bodyBytes: Bytes,
  exactSourceMessages: Count,
  exactSourceBytes: Bytes,
  attachments: Count,
  downloadedAttachments: Count,
  attachmentBytes: Bytes,
  downloadedAttachmentBytes: Bytes,
  drafts: Count,
  draftBytes: Bytes,
  draftUploads: Count,
  draftUploadBytes: Bytes,
});
export type StorageAccountCapacity = z.infer<typeof StorageAccountCapacity>;

export const StorageMailCapacity = StorageAccountCapacity.omit({
  accountId: true,
  name: true,
  email: true,
  color: true,
}).extend({ accounts: Count });
export type StorageMailCapacity = z.infer<typeof StorageMailCapacity>;

export const StorageRecoveryCapacity = z.object({
  messages: Count,
  bodyBytes: Bytes,
  rawSources: Count,
  rawSourceBytes: Bytes,
  attachments: Count,
  attachmentBytes: Bytes,
  nextPurgeAt: z.number().int().nonnegative().nullable(),
});
export type StorageRecoveryCapacity = z.infer<typeof StorageRecoveryCapacity>;

export const StorageOtherCapacity = z.object({
  contactSources: Count,
  addressBooks: Count,
  contacts: Count,
  calendarAccounts: Count,
  calendars: Count,
  calendarObjects: Count,
  eventOccurrences: Count,
  tasks: Count,
  notes: Count,
  noteAttachments: Count,
  noteAttachmentBytes: Bytes,
});
export type StorageOtherCapacity = z.infer<typeof StorageOtherCapacity>;

export const ServerStorageCapacity = z.object({
  dataDirectory: z.string(),
  diskTotalBytes: Bytes.nullable(),
  diskUsedBytes: Bytes.nullable(),
  diskAvailableBytes: Bytes.nullable(),
  databaseBytes: Bytes,
  databaseWalBytes: Bytes,
  databaseSharedMemoryBytes: Bytes,
  blobFiles: Count,
  blobBytes: Bytes,
  referencedBlobFiles: Count,
  orphanedBlobFiles: Count,
  orphanedBlobBytes: Bytes,
  missingReferencedBlobFiles: Count,
  remoteImageEntries: Count,
  remoteImageBytes: Bytes,
});
export type ServerStorageCapacity = z.infer<typeof ServerStorageCapacity>;

export const StorageCapacityResponse = z.object({
  generatedAt: z.number().int(),
  personal: z.object({
    mail: StorageMailCapacity,
    accounts: z.array(StorageAccountCapacity),
    recovery: StorageRecoveryCapacity,
    other: StorageOtherCapacity,
  }),
  server: ServerStorageCapacity.nullable(),
});
export type StorageCapacityResponse = z.infer<typeof StorageCapacityResponse>;
